/**
 * Utility functions for generating office location map URLs
 */

/**
 * Converts office data to base64 encoded string for URL
 * @param offices - Array of office objects with coordinates
 * @returns Base64 encoded string
 */
export const encodeOfficeDataToBase64 = (offices: any[]): string => {
  try {
    // Encode array directly (not wrapped in object)
    const jsonString = JSON.stringify(offices);
    const base64 = btoa(unescape(encodeURIComponent(jsonString)));
    return base64;
  } catch (error) {
    console.error('Error encoding office data:', error);
    throw error;
  }
};

/**
 * Generates a full URL for the office location map page
 * @param lat - Latitude
 * @param lng - Longitude
 * @param coordinates - Array of polygon coordinates in [lng, lat] format
 * @param baseUrl - Base URL (default: current origin)
 * @returns Full URL with lat, long, and coordinates parameters
 */
export const generateOfficeMapUrl = (lat: number, lng: number, coordinates: number[][][], baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  // Encode coordinates array to base64
  const coordinatesJson = JSON.stringify(coordinates);
  const coordinatesBase64 = btoa(unescape(encodeURIComponent(coordinatesJson)));
  return `${base}/map?lat=${lat}&long=${lng}&coordinates=${coordinatesBase64}`;
};

/**
 * Generates a full URL for the office location map page (legacy format with offices array)
 * @param offices - Array of office objects with coordinates
 * @param baseUrl - Base URL (default: current origin)
 * @returns Full URL with base64 encoded data
 */
export const generateOfficeMapUrlLegacy = (offices: any[], baseUrl?: string): string => {
  const base64Data = encodeOfficeDataToBase64(offices);
  const base = baseUrl || window.location.origin;
  return `${base}/map?data=${base64Data}`;
};

/**
 * Decodes base64 string back to office data
 * @param base64String - Base64 encoded string
 * @returns Decoded office data object
 */
export const decodeOfficeDataFromBase64 = (base64String: string): any => {
  try {
    const decodedString = atob(base64String);
    const data = JSON.parse(decodedString);
    return data;
  } catch (error) {
    console.error('Error decoding office data:', error);
    throw error;
  }
};

